import type { ProductBbox } from '@travel-planner/shared/schemas';
import type { ReactNode } from 'react';
import type {
  AdvisorDraft,
  AdvisorDraftLeg,
  AdvisorItemKind,
  AdvisorProductType,
  AdvisorRegion,
} from '../model/types';
import type { PlannerTravellers } from '../model/v3-itinerary';
import type { PlannerV3Sort } from '../model/v3-results';
import type { PlannerV3ToastState } from './planner-v3-toast';
import type { AdvisorPlannerInput } from './use-advisor-planner';
import { DEFAULT_COMMISSION_RATES } from '@travel-planner/shared/pricing';
import { plannerServiceLabel } from '@travel-planner/shared/utils/planner-day-services';
import {
  plannerAssistanceLabel,
  plannerTransferLabel,
} from '@travel-planner/shared/utils/planner-transfers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import { useDebouncedValue } from '@/shared/lib/use-debounced-value';
import {
  chapterNights,
  draftItemCount,
  newChapterNights,
  withBaseHotels,
} from '../model/add-item';
import { selectChapter } from '../model/cms-mood';
import { PlannerCommissionRatesContext } from '../model/commission';
import { toggleValue } from '../model/hotel-class';
import { regionName } from '../model/region-layout';
import { useAdvisorPlannerStore } from '../model/store';
import { addedItemDay, dayLabel } from '../model/v3-itinerary';
import { effectiveSort, nearHotelsOf, serverSortOf } from '../model/v3-results';
import { AdvisorDndShell } from './advisor-dnd-shell';
import { AdvisorMap } from './advisor-map';
import { usePlannerHost } from './planner-host';
import { PlannerV3DragPreview } from './planner-v3-drag-preview';
import { PlannerV3Filters } from './planner-v3-filters';
import { PlannerV3Itinerary } from './planner-v3-itinerary';
import { PlannerV3Results } from './planner-v3-results';
import { PlannerV3TagBar } from './planner-v3-tag-bar';
import { PlannerV3Toast } from './planner-v3-toast';
import css from './planner-v3-workspace.module.scss';
import { useAdvisorPlanner } from './use-advisor-planner';

const columnCss = pickCss(css, css.column);
const switchCss = pickCss(css, css.switch);

type NarrowPane = 'filters' | 'itinerary' | null;

type PlannerV3WorkspaceProps = Omit<
  AdvisorPlannerInput,
  'search' | 'bbox' | 'withCounts'
> & {
  /** Usually a `PlannerV3Header` built by the host. */
  header?: ReactNode;
  /** The host's actions beside the estimated total (e.g. Send to review). */
  footerActions?: ReactNode;
  /** Moods from the client brief, ringed in the Mood group. */
  briefMoods?: readonly string[];
  /** Pace from the client brief: a relaxed party is not offered long days first. */
  briefPace?: 'relaxed' | 'balanced' | 'active' | null;
  /** Countries to switch between; the host's full list when absent. */
  countryChoices?: readonly string[];
  /** Who travels — per-person prices in the estimate count once each. */
  travellers?: PlannerTravellers;
  /** The trip's first day: dates each day for supplier-rate validity (XT-828). */
  startOn?: string | null;
};

const DEFAULT_TRAVELLERS: PlannerTravellers = { adults: 2, children: 0 };

const KIND_OF_TYPE: Record<AdvisorProductType, AdvisorItemKind> = {
  hotels: 'hotel',
  experiences: 'experience',
  restaurants: 'restaurant',
};

/**
 * Mini Planner v3: Filters + map | Results | Itinerary. Narrower than
 * 1180px it shows the results, with the other two as slide-overs.
 */
export const PlannerV3Workspace = ({
  header,
  footerActions,
  briefMoods,
  briefPace,
  countryChoices,
  travellers = DEFAULT_TRAVELLERS,
  startOn = null,
  ...input
}: PlannerV3WorkspaceProps) => {
  // This layout ends the trip on a departure day after its last night.
  useEffect(() => {
    const { setDepartureDay } = useAdvisorPlannerStore.getState();
    setDepartureDay(true);
    return () => setDepartureDay(false);
  }, []);
  // The search belongs to one category: switching clears it at once, so a
  // hotel name never filters the experiences.
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState(input.type);
  if (searchType !== input.type) {
    setSearchType(input.type);
    setSearchText('');
  }
  const typedSearch = useDebouncedValue(
    `${input.type}:${searchText.trim()}`,
    300
  );
  const search = typedSearch.startsWith(`${input.type}:`)
    ? typedSearch.slice(input.type.length + 1)
    : '';
  const [mapFilter, setMapFilter] = useState(true);
  // A region or country change refits the map without a user pan, so a
  // viewport only counts for the area it was taken in.
  const areaKey = [
    input.country,
    input.activeRegionId ?? '',
    ...input.regionIds,
  ].join('|');
  const [viewport, setViewport] = useState<{
    area: string;
    bounds: ProductBbox;
  } | null>(null);
  const bounds = viewport?.area === areaKey ? viewport.bounds : null;
  const bbox = mapFilter ? bounds : null;
  const [sort, setSort] = useState<PlannerV3Sort>('relevance');
  const [hotelStars, setHotelStars] = useState<number[]>([]);
  const [hotelPriceLevels, setHotelPriceLevels] = useState<number[]>([]);
  const isHotels = input.type === 'hotels';
  const [toast, setToast] = useState<PlannerV3ToastState | null>(null);
  const clearToast = useCallback(() => setToast(null), []);
  // Closest to hotel measures from each chapter's hotel.
  const nearHotels = nearHotelsOf(useAdvisorPlannerStore((s) => s.draft));
  // The brief's dates and party: dynamic products show their contract price
  // for them (XT-951).
  const booking = useMemo(
    () =>
      startOn
        ? {
            startOn: startOn.slice(0, 10),
            nights: Math.max(1, Math.round(input.seedDurationDays ?? 1)),
            adults: travellers.adults,
            children: travellers.children,
          }
        : null,
    [startOn, input.seedDurationDays, travellers.adults, travellers.children]
  );
  const planner = useAdvisorPlanner({
    ...input,
    booking,
    briefPace,
    childrenInParty: travellers.children > 0,
    search,
    bbox,
    sort: serverSortOf(
      effectiveSort(sort, KIND_OF_TYPE[input.type]),
      nearHotels
    ),
    hotelStars: isHotels ? hotelStars : [],
    hotelPriceLevels: isHotels ? hotelPriceLevels : [],
    withCounts: true,
    onRefuse: (message) => setToast({ id: Date.now(), message }),
  });
  const [pane, setPane] = useState<NarrowPane>(null);
  const host = usePlannerHost();
  const showAvgRoomNight = host.showAvgRoomNight ?? false;
  // XT-964: one DMC plans the trip, so one set of rates for every card.
  const commissionRates =
    host.useCommissionRates?.({
      country: planner.rail.draft.legs[0]?.country ?? input.country ?? null,
      regions: planner.rail.draft.legs.map((leg) => leg.regionTitle),
    }) ?? DEFAULT_COMMISSION_RATES;
  const itemCount = draftItemCount(planner.rail.draft);
  const { onFiltersChange, page } = input;

  const lastQuery = useRef(JSON.stringify([search, bbox]));
  useEffect(() => {
    const key = JSON.stringify([search, bbox]);
    if (lastQuery.current === key) return;
    lastQuery.current = key;
    if (page > 1) onFiltersChange({ page: null });
  }, [bbox, onFiltersChange, page, search]);

  // A trip Generate books its hotels on the days; here they are the
  // chapters' base hotels.
  const { isGenerating } = planner.rail;
  const wasGenerating = useRef(false);
  useEffect(() => {
    if (wasGenerating.current && !isGenerating) {
      const { draft, replaceDraft } = useAdvisorPlannerStore.getState();
      const next = withBaseHotels(draft);
      if (next !== draft) replaceDraft(next);
    }
    wasGenerating.current = isGenerating;
  }, [isGenerating]);

  // Runs a rail change and offers to put the draft (and the waiting
  // suggestions) back as they were.
  const undoable = (
    run: () => void,
    message: (before: AdvisorDraft, after: AdvisorDraft) => string | null
  ) => {
    const { draft: before, suggestions } = useAdvisorPlannerStore.getState();
    run();
    const after = useAdvisorPlannerStore.getState().draft;
    const text = after === before ? null : message(before, after);
    if (!text) return;
    setToast({
      id: Date.now(),
      message: text,
      undo: () =>
        useAdvisorPlannerStore.getState().restore(before, suggestions),
    });
  };
  const baseHotelSet =
    (productId: string) => (_: AdvisorDraft, after: AdvisorDraft) => {
      const leg = after.legs.find(
        (entry) => entry.baseHotel?.productId === productId
      );
      if (!leg) return null;
      const nights = chapterNights(leg);
      return `Base hotel set for ${regionName(leg.regionTitle)} · ${nights} ${nights === 1 ? 'night' : 'nights'}`;
    };
  // Hotels for the chapter's region: its region becomes the active filter
  // when it is one of the country's regions.
  const findHotels = (leg: AdvisorDraftLeg) => {
    setPane(null);
    const known = planner.filterBar.regions.some(
      (region) => region.id === leg.regionId
    );
    onFiltersChange({
      type: 'hotels',
      ...(known ? selectChapter(input.regionIds, leg.regionId) : {}),
    });
  };
  // A chapter's region becomes the results' region, as picking a chapter
  // does in the XT planner.
  const showRegion = (regionId: string) => {
    setPane(null);
    onFiltersChange(selectChapter(input.regionIds, regionId));
  };
  const addRegion = (region: AdvisorRegion) => {
    undoable(
      () => {
        const { addRegion: add, draft } = useAdvisorPlannerStore.getState();
        // The first region of an empty trip takes the brief's nights.
        add(
          region,
          input.country,
          newChapterNights(draft, input.seedDurationDays)
        );
      },
      () => `Added ${regionName(region.title)}`
    );
    showRegion(region.id);
  };
  const addedTo = (before: AdvisorDraft, after: AdvisorDraft) => {
    const dayId = addedItemDay(before, after);
    const label = dayId ? dayLabel(after, dayId, regionName) : null;
    return label ? `Added to ${label}` : 'Added to the itinerary';
  };

  const closeButton = (label: string) => (
    <button
      aria-label={`Close ${label}`}
      className={css.paneClose}
      onClick={() => setPane(null)}
      type="button"
    >
      ✕
    </button>
  );
  const { inView, matching } = planner.counts;

  return (
    <PlannerCommissionRatesContext.Provider value={commissionRates}>
      <div className={css.root}>
        {header}
        <div className={css.narrowBar}>
          <button
            aria-expanded={pane === 'filters'}
            className={css.paneButton}
            onClick={() => setPane('filters')}
            type="button"
          >
            Map & filters
          </button>
          <button
            aria-expanded={pane === 'itinerary'}
            className={css.paneButton}
            onClick={() => setPane('itinerary')}
            type="button"
          >
            Itinerary · {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </button>
        </div>
        <AdvisorDndShell
          dnd={planner.dnd}
          renderPreview={(preview) => (
            <PlannerV3DragPreview
              thumbnail={preview.thumbnail}
              title={preview.title}
            />
          )}
        >
          <div className={css.columns}>
            <aside
              aria-label="Map and filters"
              className={joinCss(
                columnCss({ isOpen: pane === 'filters' }),
                css.filters
              )}
            >
              {closeButton('map and filters')}
              <PlannerV3Filters
                briefMoods={briefMoods}
                countryChoices={countryChoices}
                counts={planner.counts}
                filterBar={planner.filterBar}
                hotelFilters={{
                  stars: hotelStars,
                  priceLevels: hotelPriceLevels,
                  onToggleStars: (stars) => {
                    setHotelStars((prev) => toggleValue(prev, stars));
                    if (page > 1) onFiltersChange({ page: null });
                  },
                  onTogglePriceLevel: (level) => {
                    setHotelPriceLevels((prev) => toggleValue(prev, level));
                    if (page > 1) onFiltersChange({ page: null });
                  },
                }}
                onClear={() => {
                  setHotelStars([]);
                  setHotelPriceLevels([]);
                  planner.clearFilters();
                }}
                onSearchChange={setSearchText}
                search={searchText}
              />
              <div className={css.mapSlot}>
                <AdvisorMap
                  {...planner.map}
                  key={input.country}
                  onMoveEnd={(next) =>
                    setViewport({ area: areaKey, bounds: next })
                  }
                  geocodeCountry={false}
                  markerHover="sync"
                  regionPalette="soft"
                  showRegionPicker={false}
                />
                <div className={css.mapTools}>
                  <button
                    aria-checked={mapFilter}
                    className={css.mapFilter}
                    onClick={() => setMapFilter((prev) => !prev)}
                    role="switch"
                    type="button"
                  >
                    <span className={switchCss({ isOn: mapFilter })}>
                      <span className={css.knob} />
                    </span>
                    Only what is on the map
                  </button>
                  <p className={css.mapCount}>
                    {bbox ? (
                      <>
                        <strong>{inView}</strong> on the map · {matching ?? '…'}{' '}
                        match filters
                      </>
                    ) : (
                      <>
                        <strong>{matching ?? inView}</strong> match filters
                      </>
                    )}
                  </p>
                </div>
                <p className={css.legend}>
                  <span className={css.legendDot} /> Results
                </p>
              </div>
            </aside>
            <section
              aria-label="Results"
              className={joinCss(css.column, css.results)}
            >
              <PlannerV3Results
                toolbar={
                  <PlannerV3TagBar
                    onToggle={planner.filterBar.onSelectTag}
                    selected={planner.filterBar.selectedTags}
                    tags={planner.allTags}
                  />
                }
                draft={planner.rail.draft}
                isError={planner.productStack.isError}
                isLoading={planner.productStack.isLoading}
                items={planner.productStack.items}
                mapFiltered={Boolean(bbox)}
                matching={matching}
                onAdd={(item) =>
                  undoable(() => planner.productStack.onAdd(item), addedTo)
                }
                onAddToDay={(product, dayId) =>
                  undoable(() => planner.addToDay(product, dayId), addedTo)
                }
                onAddToNewDay={(product, region) =>
                  undoable(() => planner.addToNewDay(product, region), addedTo)
                }
                briefNights={input.seedDurationDays}
                onSetBaseHotel={(item, region, nights) =>
                  undoable(
                    () => planner.setBaseHotel(item, region, nights),
                    baseHotelSet(item.id)
                  )
                }
                regions={planner.filterBar.regions}
                kind={KIND_OF_TYPE[input.type]}
                party={{
                  adults: travellers.adults,
                  children: travellers.children,
                }}
                rooms={travellers.rooms ?? 1}
                briefMoods={briefMoods}
                onGuestsChange={planner.setProductGuests}
                onPageChange={planner.productStack.onPageChange}
                onShowAll={() => setMapFilter(false)}
                onSortChange={(next) => {
                  setSort(next);
                  if (page > 1) onFiltersChange({ page: null });
                }}
                page={page}
                products={planner.products}
                showAvgRoomNight={showAvgRoomNight}
                sort={sort}
                total={inView}
              />
            </section>
            <div
              className={joinCss(
                columnCss({ isOpen: pane === 'itinerary' }),
                css.itinerary
              )}
            >
              {closeButton('itinerary')}
              <PlannerV3Itinerary
                startOn={startOn}
                draft={planner.rail.draft}
                footerActions={footerActions}
                generateError={planner.rail.generateError}
                generatingDayId={planner.rail.generatingDayId}
                isGenerating={planner.rail.isGenerating}
                onAddDay={planner.rail.onAddDay}
                onAcceptSuggestion={(dayId, item) =>
                  undoable(
                    () => planner.suggestions.onAccept(dayId, item),
                    addedTo
                  )
                }
                onDismissSuggestions={planner.suggestions.onDismiss}
                onGenerate={planner.rail.onGenerate}
                onGenerateDay={planner.suggestions.onGenerate}
                onSkipSuggestion={planner.suggestions.onSkip}
                suggestions={planner.suggestions.byDay}
                pendingDetailIds={planner.itemDetails.pendingIds}
                onRemoveItem={(item) =>
                  undoable(
                    () => planner.rail.onRemoveItem(item.id),
                    () => `Removed · ${item.title}`
                  )
                }
                onRemoveService={(dayId, service) =>
                  undoable(
                    () => planner.removeDayService(dayId, service),
                    () => `Removed · ${plannerServiceLabel(service)}`
                  )
                }
                onTransportVehiclesChange={planner.setDayTransportVehicles}
                onTransportSpanChange={planner.setDayTransportSpan}
                onRestoreService={planner.restoreDayService}
                onTransferChange={{
                  remove: (dayId, kind, vehicle) =>
                    undoable(
                      () => planner.removeTransferLine(dayId, kind, vehicle),
                      () =>
                        `Removed · ${plannerTransferLabel(kind)}${vehicle ? ` · ${vehicle}` : ''}`
                    ),
                  restore: planner.restoreTransfer,
                  vehicles: planner.setTransferVehicles,
                  place: planner.setTransferPlace,
                  time: planner.setTransferTime,
                  assistance: (dayId, kind, assistance) =>
                    assistance
                      ? planner.setTransferAssistance(dayId, kind, assistance)
                      : undoable(
                          () =>
                            planner.setTransferAssistance(dayId, kind, null),
                          () => `Removed · ${plannerAssistanceLabel(kind)}`
                        ),
                }}
                onCommentChange={planner.setItemComment}
                onHotelBookingChange={planner.setHotelBooking}
                onTimeChange={planner.setItemTime}
                onGuestsChange={planner.setProductGuests}
                onAddRegion={addRegion}
                onFindHotels={findHotels}
                onShowRegion={(leg) => showRegion(leg.regionId)}
                regions={planner.filterBar.regions}
                onRemoveDay={(day, number) =>
                  undoable(
                    () => planner.removeDay(day.id),
                    () => `Removed Day ${number}`
                  )
                }
                onRemoveBaseHotel={(leg) =>
                  undoable(
                    () => planner.removeBaseHotel(leg.id),
                    () => `Removed · ${leg.baseHotel?.title ?? 'base hotel'}`
                  )
                }
                onRemoveLeg={(leg) =>
                  undoable(
                    () => planner.rail.onRemoveLeg(leg.id),
                    () => `Removed ${regionName(leg.regionTitle)}`
                  )
                }
                onRemoveSuggested={() => planner.rail.onRemoveSuggested(null)}
                travellers={travellers}
              />
            </div>
          </div>
        </AdvisorDndShell>
        {pane ? (
          <div
            aria-hidden="true"
            className={css.scrim}
            onClick={() => setPane(null)}
          />
        ) : null}
        {toast ? (
          <PlannerV3Toast key={toast.id} onDone={clearToast} toast={toast} />
        ) : null}
      </div>
    </PlannerCommissionRatesContext.Provider>
  );
};
